import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ASSETS, BRAND } from "@/lib/brand";
import { Dumbbell, Salad, CheckCircle2, Sparkles, ArrowRight, Moon } from "lucide-react";
import { Link } from "react-router-dom";

export default function ClientToday() {
  const { user } = useAuth();
  const [stats, setStats] = useState({});
  const [cycle, setCycle] = useState(null);
  useEffect(() => {
    api.get("/client/overview").then((r) => setStats(r.data)).catch(() => {});
    api.get("/cycle").then((r) => setCycle(r.data)).catch(() => {});
  }, []);

  const firstName = user?.name?.split(" ")[0] || "friend";

  return (
    <div className="space-y-12 lg:mt-0 mt-10" data-testid="client-today">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "var(--surface-2)" }}>
        <img src={ASSETS.brandedBodysuit} alt="" className="w-full h-[420px] object-cover object-top opacity-90" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.4) 55%, rgba(255,255,255,0) 100%)" }} />
        <div className="absolute inset-0 p-8 md:p-14 flex flex-col justify-center max-w-2xl">
          <p className="overline">Today, with {BRAND.name}</p>
          <h1 className="font-display text-4xl sm:text-5xl mt-6 leading-tight" style={{ color: "var(--ink)" }}>
            Hello,<br />
            <span className="font-script normal-case" style={{ fontSize: "1.15em", color: "var(--accent)", letterSpacing: 0 }}>{firstName}.</span>
          </h1>
          <div className="bronze-rule mt-6" />
          <p className="font-soft text-lg mt-6" style={{ color: "var(--text-secondary)" }}>
            One intentional choice today nourishes a thousand tomorrows.
          </p>
        </div>
      </section>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "var(--border-strong)" }}>
        {[
          { label: "Rituals today", value: `${stats.checkins_today || 0}/${stats.habits || 0}`, icon: CheckCircle2 },
          { label: "Nourishment", value: stats.meals_today || 0, sub: "meals logged", icon: Salad },
          { label: "Programs", value: stats.programs || 0, icon: Dumbbell },
          { label: "Last weigh-in", value: stats.latest_weight ? `${stats.latest_weight}` : "—", sub: stats.latest_weight ? "lb" : "", icon: Sparkles },
        ].map((k) => (
          <div key={k.label} className="p-7" style={{ background: "var(--surface-1)" }}>
            <k.icon size={14} style={{ color: "var(--accent)" }} />
            <div className="overline mt-5">{k.label}</div>
            <div className="font-display text-3xl mt-2">{k.value}</div>
            {k.sub && <div className="font-label text-[10px] tracking-widest uppercase mt-1" style={{ color: "var(--text-tertiary)" }}>{k.sub}</div>}
          </div>
        ))}
      </div>

      {/* Cycle strip */}
      <Link to="/app/cycle" data-testid="today-cycle-widget"
        className="block p-7 transition-colors hover:bg-surface-2"
        style={{ background: "var(--surface-1)", border: "1px solid var(--border-strong)" }}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          {cycle?.configured && cycle.cycle_day ? (
            <div>
              <p className="overline flex items-center gap-2"><Moon size={12} style={{ color: "var(--accent)" }} /> My cycle</p>
              <h3 className="font-display text-2xl mt-2">{cycle.guide?.label} phase</h3>
              <p className="font-soft text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                Day {cycle.cycle_day} · next period est. {cycle.next_period}
              </p>
            </div>
          ) : (
            <div>
              <p className="overline flex items-center gap-2"><Moon size={12} style={{ color: "var(--accent)" }} /> My cycle</p>
              <h3 className="font-display text-2xl mt-2">Tune into your cycle</h3>
              <p className="font-soft text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                Set it up in a few taps — food & training ideas for the body you're in today.
              </p>
            </div>
          )}
          <div className="font-label text-[10px] tracking-[0.26em] uppercase flex items-center gap-2" style={{ color: "var(--accent)" }}>
            {cycle?.configured ? "Check in" : "Set up"} <ArrowRight size={11} />
          </div>
        </div>
      </Link>

      {/* Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-px" style={{ background: "var(--border-strong)" }}>
        {[
          { to: "/app/workouts", testid: "quick-workouts", eyebrow: "Up next", title: "Today's session", body: "Step into your assigned program." },
          { to: "/app/nutrition", testid: "quick-nutrition", eyebrow: "Nourish", title: "Log a meal", body: "Macros, mineral-rich foods, recipes." },
          { to: "/app/ai", testid: "quick-ai", eyebrow: "Guidance", title: "Ask Aurelis", body: "Your AI co-coach. Always on call." },
        ].map((q) => (
          <Link key={q.to} to={q.to} data-testid={q.testid}
            className="p-10 block transition-colors hover:bg-surface-2 group"
            style={{ background: "var(--surface-1)" }}>
            <p className="overline">{q.eyebrow}</p>
            <h3 className="font-display text-2xl mt-5 leading-tight">{q.title}</h3>
            <div className="bronze-rule mt-4" />
            <p className="font-soft text-base mt-5" style={{ color: "var(--text-secondary)" }}>{q.body}</p>
            <div className="mt-6 font-label text-[10px] tracking-[0.26em] uppercase flex items-center gap-2" style={{ color: "var(--accent)" }}>
              Open <ArrowRight size={11} />
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
